/**
 * todosApi.ts — thin client wrappers for the engine's /api/todos routes.
 *
 * Per refactor/engine-api-contract-for-graduation.md §A.2. Lives outside
 * frozen api.ts; mirrors services/dealsApi.ts (G1).
 *
 * Dates returned by the engine are SQLite-native — call parseEngineDate /
 * engineDateToIso from utils/engineDate.ts when normalization is needed.
 * These wrappers pass through the raw envelope.
 */
import { api } from './apiClient';

/** Raw todo shape returned by the engine — `todos.*` columns + the few
 *  joined display fields (assigned_name, client_name, status_label,
 *  status_color, completed_by_name) that the route's SELECT pulls in. */
export interface TodoRow {
  id: number;
  title: string;
  description: string | null;
  category: string;
  source_type: string;
  source_id: string | null;
  task_type: string;
  status_id: number | null;
  status_label?: string | null;
  status_color?: string | null;
  is_completed?: boolean;
  priority: string;
  assigned_to: number | null;
  assigned_name?: string | null;
  created_by: number | null;
  creator_name?: string | null;
  /** 2026-10-03: who put this to-do on its owner's list — the latest
   *  assignment in the activity log, else the creator. Null when the app
   *  made it with nobody behind it (todoService ASSIGNED_BY_SQL). */
  assigned_by?: number | null;
  assigned_by_name?: string | null;
  client_card_id: string | null;
  client_name?: string | null;
  /** Bug-fix 2026-05-29: server-side join brings the source row's display
   *  name forward (currently only set when source_type='deal' — see
   *  todoService.TODO_SELECT). Null otherwise. Used by the /todos table's
   *  Card column to show "Acme Co" instead of "Deal #123". */
  source_label?: string | null;
  /** Bug-fix 2026-05-29: server-side join to deals.pipeline_id → pipelines.name
   *  so the /todos Board column populates for deal-attached todos. Null
   *  for non-deal source types (process / ticket — deferred). */
  board_name?: string | null;
  /** P2-2: the owning rock's id for eos_rock / eos_rock_milestone todos —
   *  source_id alone is the milestone id, which has no route. Drives the
   *  /eos/rocks/:id back-link. Null for every other source type. */
  eos_rock_id?: string | null;
  /** Sprint X2: the source row's status/stage key — populated only for
   *  source_type='project' (projects.status, the 13-stage spine key).
   *  The adapter resolves the display label from PROJECTS_BOARD_CONFIG
   *  for the "Project: <title> · <stage>" chip. Null otherwise. */
  source_status?: string | null;
  due_date: string | null;
  completed_at: string | null;
  completed_by: number | null;
  completed_by_name?: string | null;
  recurrence_type: string;
  recurrence_config: string | null;
  recurrence_end_date: string | null;
  recurrence_end_count: number | null;
  parent_recurring_id: number | null;
  notes: string | null;
  url: string | null;
  workflow_link: string | null;
  relevance_score: number | null;
  created_at: string;
  updated_at: string;
  // Phase 7c c1.2 fix: surface the c1 contract columns through the API.
  // The c1.1 engineDataLayer mapping read `(row as { state?: string })
  // .state === 'skipped'` but `state` wasn't on this TodoRow type AND
  // wasn't on backend `mapTodoRow`'s output — so it was always undefined
  // at runtime, the cast silently failed, and skipped rows reverted to
  // non-skipped after every list refresh. Both ends extended in c1.2.
  state?: string | null;
  failure_reason?: string | null;
  skipped_at?: string | null;
  skipped_by?: number | null;
  automation_enabled?: boolean;
  automation_fires_at?: string | null;
  // Entry 72: true when the todo has an unsent composer draft (todo_drafts).
  // Drives the row "draft" indicator.
  has_draft?: boolean;
  /** CD-25: the ticket FK, for the SOURCE row's ticket back-link. The column
   *  has existed since chunk_a3 but mapTodoRow stripped it. */
  ticket_id?: number | null;
  /** CD-25: the CURATED projection of `app_request_meta` — which call this
   *  to-do books. The raw column also carries the privileged host binding
   *  (host_user_id / host_mode), which is deliberately NOT sent to the client
   *  and is written only by the server from the live Stage Playbook row. */
  app_meta?: { call_type: string | null; event_template_id: number | null; workflow_send: boolean };
  // ── The to-dos upgrade (state.md Entry 466) ─────────────────────────
  /** When it was pinned to the top of its owner's list. Null = not pinned. */
  pinned_at?: string | null;
  /** 'ops' | 'sales' | 'marketing' | 'admin' | null. */
  team?: TodoTeam | null;
  /** The due date is the app's one-week suggestion, not a date a person chose. */
  due_suggested?: boolean;
  /** Who an email / text / call to-do goes to, when someone picked them. */
  contact_id?: number | null;
  contact_name?: string | null;
  /** Comm rows only: the recipient the composer will open with, and — when it
   *  cannot run — the one sentence the disabled verb shows. */
  target?: TodoTarget | null;
  target_missing?: string | null;
}

export type TodoTeam = 'ops' | 'sales' | 'marketing' | 'admin';

export interface TodoTarget {
  contact_id: number;
  name: string;
  email: string | null;
  phone: string | null;
  /** True when a person picked this contact on the to-do itself. */
  explicit: boolean;
}

/** GET /todos/link-targets — what a to-do on a client can link to or go to. */
export interface TodoLinkTargets {
  projects: Array<{ id: number; title: string; status: string | null; done: boolean }>;
  deals: Array<{ id: number; name: string; stage: string | null; status: string | null }>;
  tickets: Array<{ id: number; title: string; status: string | null }>;
  contacts: Array<{ id: number; name: string; email: string | null; phone: string | null }>;
}

export type TodoRecordKind = 'deal' | 'project' | 'ticket' | 'eos_l10' | 'eos_issue' | 'eos_rock';

export interface TodoContextRecord {
  kind: TodoRecordKind;
  id: string;
  label: string;
  source_type: string;
  source_id: string;
  ticket_id?: number | null;
}

/** GET /todos/context — what a new to-do made from a record's page starts with. */
export interface TodoPageContext {
  client_card_id: string | null;
  client_name: string | null;
  record: TodoContextRecord | null;
  contact: { id: number; name: string; email: string | null; phone: string | null } | null;
  team: TodoTeam | null;
  department?: string | null;
}

/** CD-25: a pickable call type, with the booking rule it carries. `floor_field`
 *  names the PROJECT date the call can't precede (Delivery Call →
 *  `deliver_by`); `floor_field_label` is the same thing in the user's words. */
export interface TodoCallTypeOption {
  event_template_id: number;
  key: string;
  name: string;
  duration_minutes: number;
  host_role: string;
  floor_field: string | null;
  floor_field_label: string | null;
  /** `operations` (a call the projects pipeline books against) | `custom` (one
   *  somebody made for themselves). Drives the call picker's grouping. */
  kind: string;
}

/**
 * CD-25: what a scheduling to-do books, and whether its date rule can be
 * applied right now. The container renders this verbatim so the rule is visible
 * BEFORE the composer opens — `earliest_bookable` is the date the client will
 * actually see as their first option (deliver-by + 1 business day for the
 * Delivery Call), and `blocker` is the one honest sentence when it can't apply.
 */
export interface TodoSchedulingInfo {
  todo_id: number;
  task_type: string;
  call_type: string | null;
  event_template_id: number | null;
  call_type_name: string | null;
  duration_minutes: number | null;
  floor_field: string | null;
  floor_field_label: string | null;
  floor_rule: string | null;
  project_id: number | null;
  project_title: string | null;
  floor_date: string | null;
  earliest_bookable: string | null;
  /** True when the call type came from the Stage Playbook row rather than this
   *  to-do's own binding. The composer resolves it either way, so the container
   *  shows it as bound (with provenance) rather than claiming none is picked. */
  from_playbook: boolean;
  /** The exact person's link this to-do books on (null = the call type picks its host). */
  event_type_id?: number | null;
  link_name?: string | null;
  host_user_id?: number | null;
  host_name?: string | null;
  ready: boolean;
  blocker: string | null;
}

/** POST /todos/schedule-call — the to-do a Schedule call books on. */
export interface StartScheduleCallResult {
  todo: TodoRow;
  /** True when an open to-do already asked for this call and was reused
   *  (the playbook's, typically) — no new to-do was made. */
  adopted: boolean;
  scheduling: TodoSchedulingInfo | null;
}

/** Entry 72: a composer draft stored against a todo_id. `content` is the
 *  composer state — email: { subject, bodyHtml, to[], cc[], bcc[] };
 *  sms: { to, body }. */
export interface TodoDraft {
  todo_id: number;
  channel: 'email' | 'sms';
  content: Record<string, any> | null;
  updated_at: string;
}

/** GET /:id additionally inlines the todo's activity log. */
export interface TodoFull extends TodoRow {
  activity?: Array<Record<string, unknown>>;
}

export interface ListTodosParams {
  category?: string;
  source_type?: string;
  /** G1.3: filter to todos for one source row (e.g. deal id when
   *  source_type='deal'). Mirrors the backend filter added in G1.3. */
  source_id?: string;
  status_id?: number;
  priority?: string;
  assigned_to?: number;
  client_card_id?: string;
  task_type?: string;
  search?: string;
  is_completed?: boolean;
  due_before?: string;
  due_after?: string;
  page?: number;
  perPage?: number;
  sortField?: string;
  sortDir?: 'asc' | 'desc';
  groupBy?: string;
  /** The to-dos upgrade: 'ops' | 'sales' | 'marketing' | 'admin' | 'none'. */
  team?: string;
  /** Completed on or after (SQLite datetime / ISO) — the Done rail's week. */
  completed_after?: string;
  /** Who put it on its owner's list — "Assigned by me". */
  assigned_by?: number;
}

/** What POST /api/todos accepts. Whitelisted server-side via todoService. */
export interface CreateTodoInput {
  title: string;
  description?: string;
  category?: string;
  source_type?: string;
  source_id?: string;
  task_type?: string;
  status_id?: number | null;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  assigned_to?: number | null;
  client_card_id?: string | null;
  due_date?: string | null;
  notes?: string;
  url?: string;
  workflow_link?: string;
  /** JSON blob the action layer reads back. Scheduling todos carry
   *  {call_type, event_template_id} so the composer can mint the right
   *  booking link (projectTodoEmailService.resolveScheduling). */
  app_request_meta?: string;
  // The to-dos upgrade (Entry 466).
  /** Omitted = the source's team; null = deliberately no team. */
  team?: TodoTeam | null;
  due_suggested?: boolean;
  contact_id?: number | null;
  pinned?: boolean;
  recurrence_type?: string;
  recurrence_config?: string | null;
  department?: string | null;
}

/** Partial-update shape accepted by PATCH /api/todos/:id. */
export type UpdateTodoInput = Partial<{
  title: string;
  description: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status_id: number | null;
  assigned_to: number | null;
  due_date: string | null;
  is_completed: boolean;
  notes: string;
  url: string;
  workflow_link: string;
  task_type: string;
  category: string;
  client_card_id: string | null;
  // The to-dos upgrade (Entry 466).
  team: TodoTeam | null;
  due_suggested: boolean;
  contact_id: number | null;
  source_type: string;
  source_id: string | null;
  ticket_id: number | null;
  recurrence_type: string;
  recurrence_config: string | null;
  /** Names the gesture behind the edit for the History ("snooze"). */
  _via: string;
}>;

type SuccessEnvelope<T> = { success: true; data: T };

export const todosApi = {
  /** GET /api/todos with optional filters. Returns array only (engine
   *  drops the count — gap G.3). */
  list: (params?: ListTodosParams): Promise<SuccessEnvelope<TodoRow[]>> =>
    api.get('/todos', { params }).then((r) => r.data),

  /** GET /api/todos/:id — includes activity log inlined. */
  get: (id: number | string): Promise<SuccessEnvelope<TodoFull>> =>
    api.get(`/todos/${id}`).then((r) => r.data),

  /** POST /api/todos — idempotent via server-side middleware when caller
   *  supplies an Idempotency-Key header. */
  create: (input: CreateTodoInput): Promise<SuccessEnvelope<TodoRow>> =>
    api.post('/todos', input).then((r) => r.data),

  /** PATCH /api/todos/:id — partial update. When `is_completed=true` is
   *  set, server cascades to processService.completeTask /
   *  ticketService.closeTicket if the todo's source_type is 'process'
   *  or 'ticket'. */
  update: (id: number | string, input: UpdateTodoInput): Promise<SuccessEnvelope<TodoRow>> =>
    api.patch(`/todos/${id}`, input).then((r) => r.data),

  /** DELETE /api/todos/:id — hard delete. Returns `{ deleted: number }`. */
  remove: (id: number | string): Promise<SuccessEnvelope<{ deleted: number }>> =>
    api.delete(`/todos/${id}`).then((r) => r.data),

  // ── CD-25: the call-type binding + its booking rule ─────────────────
  // A "schedule call" to-do books a SPECIFIC call, and that choice carries a
  // date rule read off the linked project. These three let any to-do surface
  // pick the call, read the rule, and say honestly when it can't be applied.

  /** GET /todos/call-types — the pickable call types, each with its floor rule. */
  listCallTypes: (): Promise<SuccessEnvelope<TodoCallTypeOption[]>> =>
    api.get('/todos/call-types').then((r) => r.data),

  /** GET /todos/:id/scheduling — what this to-do books + whether it's ready. */
  getScheduling: (id: number | string): Promise<SuccessEnvelope<TodoSchedulingInfo>> =>
    api.get(`/todos/${id}/scheduling`).then((r) => r.data),

  /** PUT /todos/:id/scheduling — bind (or clear, with null) the call type.
   *  Curated on purpose: the server merges only the call keys, preserving the
   *  privileged host binding seeded from Settings → Projects. */
  /** Bind the call. `eventTypeId` = one PERSON'S booking link (the call-link
   *  picker) — it names the calendar and wins over the template. */
  setCallType: (id: number | string, eventTemplateId: number | null, eventTypeId?: number | null): Promise<SuccessEnvelope<TodoSchedulingInfo>> =>
    api.put(`/todos/${id}/scheduling`, { event_template_id: eventTemplateId, ...(eventTypeId != null ? { event_type_id: eventTypeId } : {}) }).then((r) => r.data),

  /** POST /todos/schedule-call — Schedule call's to-do on a project. ADOPTS the
   *  open to-do already asking for the picked call (the Stage Playbook's
   *  first) and binds the link to it, else creates one titled `title`;
   *  `adopted` says which. Use this, not create + setCallType, for a Schedule
   *  call action — it is what keeps a project to one to-do per call. */
  startScheduleCall: (input: { source_type: 'project'; source_id: string; event_type_id: number; title?: string }): Promise<SuccessEnvelope<StartScheduleCallResult>> =>
    api.post('/todos/schedule-call', input).then((r) => r.data),

  /** POST /:id/complete — shortcut for is_completed=true with the same
   *  source-type cascade as PATCH. */
  complete: (id: number | string): Promise<SuccessEnvelope<TodoRow>> =>
    api.post(`/todos/${id}/complete`).then((r) => r.data),

  /** POST /:id/reopen — clears is_completed + completed_at. */
  reopen: (id: number | string): Promise<SuccessEnvelope<TodoRow>> =>
    api.post(`/todos/${id}/reopen`).then((r) => r.data),

  /** The to-dos upgrade: POST /:id/pin — pin to / unpin from the top of the
   *  owner's list (the Priority section). */
  pin: (id: number | string, pinned: boolean): Promise<SuccessEnvelope<TodoRow>> =>
    api.post(`/todos/${id}/pin`, { pinned }).then((r) => r.data),

  /** GET /todos/link-targets — a client's projects, deals, tickets and contacts
   *  (the SOURCE band's record picker + the "who it goes to" picker). */
  linkTargets: (clientCardId: string): Promise<SuccessEnvelope<TodoLinkTargets>> =>
    api.get('/todos/link-targets', { params: { client_card_id: clientCardId } }).then((r) => r.data),

  /** GET /todos/context — what a new to-do made on a record's page starts with
   *  (the top bar's "+ Add a to-do"). */
  pageContext: (kind: string, id: string): Promise<SuccessEnvelope<TodoPageContext>> =>
    api.get('/todos/context', { params: { kind, id } }).then((r) => r.data),

  /** POST /bulk — body `{ ids, action, data? }`. Actions: complete | reopen
   *  | delete | update. Returns `{ updated: number, errors: string[] }`. */
  bulk: (
    ids: number[],
    action: 'complete' | 'reopen' | 'delete' | 'update' | 'archive' | 'unarchive',
    data?: Record<string, unknown>,
  ): Promise<SuccessEnvelope<{ updated: number; errors: string[] }>> =>
    api.post('/todos/bulk', { ids, action, data }).then((r) => r.data),

  // ── Phase 7c c1: action-task contract endpoints ─────────────────────
  // Drive comm action-tasks (email/sms/phone) through the contract
  // state machine. Plain todo + form continue using /complete (which
  // is now ALSO contract-gated server-side — see actionTaskService).

  /** POST /:id/fire-action — record action_executed transition. For
   *  email/sms, the composer (7c c2) calls this AFTER its real send
   *  succeeded; for phone, it's the "I just placed this call" log
   *  path. */
  fireAction: (id: number | string): Promise<SuccessEnvelope<TodoRow>> =>
    api.post(`/todos/${id}/fire-action`).then((r) => r.data),

  /** POST /:id/skip — skip with the existing warning UX. Sets
   *  state='skipped' + skipped_at + skipped_by. Idempotent. */
  skip: (id: number | string): Promise<SuccessEnvelope<TodoRow>> =>
    api.post(`/todos/${id}/skip`).then((r) => r.data),

  /** POST /:id/reactivate — un-skip. state → 'pending', skipped_at /
   *  skipped_by cleared. The contract always had this transition; the route
   *  was only mounted 2026-09-22 (deferred-2026-09-154), which is why Skip
   *  used to be a one-way door. */
  reactivate: (id: number | string): Promise<SuccessEnvelope<TodoRow>> =>
    api.post(`/todos/${id}/reactivate`).then((r) => r.data),

  /** POST /:id/schedule — arm a fire time. fires_at is ISO 8601.
   *  Phase 7c c2.5b: automationEnabled = auto-send (true, default) vs
   *  reminder (false). Re-schedule (changing the time) is now allowed. */
  schedule: (
    id: number | string,
    fires_at: string,
    automationEnabled: boolean = true,
  ): Promise<SuccessEnvelope<TodoRow>> =>
    api.post(`/todos/${id}/schedule`, { fires_at, automation_enabled: automationEnabled }).then((r) => r.data),

  /** Phase 7c c2.5b: POST /:id/automation — flip auto-send ↔ reminder on
   *  an already-scheduled task WITHOUT changing the fire time (the ⚡
   *  toggle). Preserves automation_fires_at, so the time/date survives. */
  setAutomation: (
    id: number | string,
    enabled: boolean,
  ): Promise<SuccessEnvelope<TodoRow>> =>
    api.post(`/todos/${id}/automation`, { enabled }).then((r) => r.data),

  /** POST /:id/unschedule — cancel the schedule entirely (→ pending). */
  unschedule: (id: number | string): Promise<SuccessEnvelope<TodoRow>> =>
    api.post(`/todos/${id}/unschedule`).then((r) => r.data),

  /** Phase 7c c2: GET /:id/composer-context — returns the merge-field
   *  blob (primary contact, deal, client, sender) the composer renders
   *  tokens against on load + send. See backend
   *  `composerMergeResolver.getComposerContext` for the shape. */
  composerContext: (id: number | string): Promise<SuccessEnvelope<ComposerContextResponse>> =>
    api.get(`/todos/${id}/composer-context`).then((r) => r.data),

  /** QoL (2026-06-02): composer-context for a contact summoned from a
   *  card's contact tile — no todo, resolved from the card's deal +
   *  the chosen contact. Powers token resolution + recipient association
   *  when the email/SMS composer is opened from the contact tile (manual
   *  mode). GET /todos/contact-composer-context?card_id=&contact_id=. */
  composerContextForCardContact: (
    cardId: number | string,
    contactId: number | null,
    // 2026-08-13: which table the id belongs to. projects.id and deals.id are
    // separate sequences — resolving a project id as a deal is the Christopher
    // Wood bug (empty composer, or a colliding deal's contacts). Defaults to
    // 'deal' server-side for legacy callers.
    cardKind?: 'deal' | 'project',
  ): Promise<SuccessEnvelope<ComposerContextResponse>> =>
    api
      .get(`/todos/contact-composer-context`, {
        params: { card_id: cardId, contact_id: contactId ?? undefined, card_kind: cardKind },
      })
      .then((r) => r.data),

  /** Phase 7c c2: POST /:id/record-manual-failure — composer catch-block
   *  entry point. Called when the user clicks Send and the upstream
   *  send pipeline (gmailService.sendEmail) throws. State stays
   *  pending, failure_reason attached, paprika failure chip renders. */
  recordManualFailure: (
    id: number | string,
    failure_reason: ContractFailureReason,
  ): Promise<SuccessEnvelope<TodoRow>> =>
    api.post(`/todos/${id}/record-manual-failure`, { failure_reason }).then((r) => r.data),

  // ── Entry 72: composer draft store (keyed by todo_id) ───────────────
  // Persists the composer's working content so schedule-manual keeps the
  // composed message — restored on reopen, cleared on send.

  /** GET /:id/draft — the composer draft for a task, or null if none. */
  getDraft: (id: number | string): Promise<SuccessEnvelope<TodoDraft | null>> =>
    api.get(`/todos/${id}/draft`).then((r) => r.data),

  /** PUT /:id/draft — upsert the composer draft (last-write-wins). */
  saveDraft: (
    id: number | string,
    channel: 'email' | 'sms',
    content: Record<string, unknown>,
  ): Promise<SuccessEnvelope<TodoDraft>> =>
    api.put(`/todos/${id}/draft`, { channel, content }).then((r) => r.data),

  /** DELETE /:id/draft — clear the draft (called on send). */
  clearDraft: (id: number | string): Promise<SuccessEnvelope<{ cleared: number }>> =>
    api.delete(`/todos/${id}/draft`).then((r) => r.data),
};

// ─── Phase 7c c2: composer-context shape ──────────────────────────────

export interface ComposerContextContact {
  id: number | null;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  email: string | null;
  phone: string | null;
}

export interface ComposerContextDeal {
  id: number | null;
  title: string | null;
  value: number | null;
  stage: string | null;
  pipeline_id: number | null;
}

export interface ComposerContextClient {
  card_id: string | null;
  name: string | null;
}

export interface ComposerContextUser {
  id: number | null;
  full_name: string | null;
  email: string | null;
}

/** Phase 7c c2.5b: a user-defined custom field on the deal, for
 *  `{{deal.custom.<name>}}` tokens. `name` is the machine name used in
 *  the token; `label` is the human label for the picker. */
export interface ComposerContextCustomField {
  name: string;
  label: string;
  value: string | null;
}

export interface ComposerContextResponse {
  todo_id: number;
  task_type: string;
  primary_contact: ComposerContextContact | null;
  all_contacts: ComposerContextContact[];
  deal: ComposerContextDeal | null;
  client: ComposerContextClient | null;
  user: ComposerContextUser | null;
  // Phase 7c c2.5b: present when the todo is on a deal with custom fields.
  deal_custom_fields: ComposerContextCustomField[];
}

// Mirror the backend `FailureReason` closed enum from
// `backend/src/contracts/actionTaskContract.ts`. The composer's
// catch-block classifies upstream errors into one of these reasons
// before calling recordManualFailure.
export type ContractFailureReason =
  | 'missing_contact_info'
  | 'merge_field_safety_catch'
  | 'inbox_not_synced'
  | 'recipient_opted_out'
  | 'parent_paused'
  | 'rate_limited'
  | 'system_error';
